import React, { useState, useEffect, useMemo } from 'react';
import { Footer, Header } from './headerFooter';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { EDUC_OPTIONS } from './constants';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import { getHost } from './config';
import FilterSidebar from './FilterSidebar';
import { initialSidebarOpen, collapseFilterSidebarOnMobile, joinFilterSummaryParts } from './filterSidebarUtils';
import './custom.scss';

/** Min/max come as null from API when all ΜΟΡΙΑ in the group are missing. */
function formatTransferMinMaxRange(min, max) {
    if (min == null && max == null) return '—';
    return `${min ?? '—'}-${max ?? '—'}`;
}

function TransferData() {

    const navigate = useNavigate();
    const [educType, setEducType] = useState('');
    const [specialtyOptions, setSpecialtyOptions] = useState([]);
    const [specialty, setSpecialty] = useState('');
    const [specialCategoriesIncluded, setSpecialCategoriesIncluded] = useState(true);
    const [data, setData] = useState(null);
    const [uniqueDates, setUniqueDates] = useState([]);
    const [uniqueLocations, setUniqueLocations] = useState([]);
    const [loading, setLoading] = useState(false); // New loading state
    const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
        }
    }, [token, navigate]);

    const handleLogout = () => {
        // Clear the authToken from localStorage
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());

        // Redirect to the login page
        navigate('/', { replace: true });
    }

    useEffect(() => {
            if (educType) { // Fetch only if yearRange is selected
                const url = `${getHost()}/api/specialty-types/transfers?educ_type=${educType}`;
                fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                })
                    .then(response => response.json())
                    .then(data => setSpecialtyOptions(data.results))
                    .catch(error => console.error('Error fetching specialties:', error));
            } else {
                setSpecialtyOptions([]); // Clear specialties when yearRange is not selected
            }
        }, [educType]);

        const handleSubmit = () => {
            setLoading(true);
            const url = `${getHost()}/api/transfers`;
            const requestBody = {
                specialty: specialty,
                educ_type: educType,
                special_category_included: specialCategoriesIncluded
            };

            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                cache: 'no-store',
                body: JSON.stringify(requestBody),
            })
                .then(response => response.json())
                .then(data => {
                    const results = data.results;
                    const dates = Object.keys(results);
                    const locations = new Set();

                    const tableData = [];

                    dates.forEach(date => {
                        const locs = results[date];
                        Object.entries(locs).forEach(([location, stats]) => {
                            locations.add(location);
                            tableData.push({
                                date,
                                location,
                                count: stats.count,
                                min: stats.min,
                                max: stats.max
                            });                            
                        });
                    });
                    
                    setData(tableData);
                    setUniqueDates(dates);
                    setUniqueLocations(Array.from(locations).sort());
                    collapseFilterSidebarOnMobile(setSidebarOpen);
                })
                .catch(error => console.error('Error fetching data:', error))
                .finally(() => {
                    setLoading(false); // Hide loading indicator regardless of success/failure
                });

        };
    
    const isFormValid = educType !== '' && specialty !== '';

    const filterSummary = useMemo(() => {
        const educLabel = EDUC_OPTIONS.find((option) => option.value === educType)?.label || educType;
        return joinFilterSummaryParts([educLabel, specialty]);
    }, [educType, specialty]);

    return (
        <div id="root">
            <Header handleLogout={handleLogout}/>
            <main className='main-container ranking-per-state-page'>
                <FilterSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    filterSummary={filterSummary}
                    bodyId="transfer-data-sidebar-body"
                >
                        <Form>
                            <Form.Group className="mb-3 pt-3">
                                <Form.Label className='custom-label'>Τύπος εκπαίδευσης</Form.Label>
                                <Form.Select
                                    className="custom-form"
                                    name="educ_type"
                                    value={educType}
                                    onChange={(e) => {
                                        setEducType(e.target.value);
                                        setSpecialty("");
                                        setData(null);
                                    }}
                                    required
                                >
                                    <option value="">Επιλέξτε...</option>
                                    {EDUC_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className='custom-label'>Ειδικότητα:</Form.Label>
                                <Form.Select
                                    className="custom-form"
                                    value={specialty}
                                    onChange={(e) => setSpecialty(e.target.value)}
                                    disabled={!educType}
                                >
                                    <option>Επιλέξτε...</option>
                                    {specialtyOptions.length > 0 ? (
                                        specialtyOptions
                                            .slice()
                                            .sort((a, b) => a.localeCompare(b))
                                            .map((spec) => (
                                                <option key={spec} value={spec}>
                                                    {spec}
                                                </option>
                                            ))
                                    ) : (
                                        <option>No specialties available</option>
                                    )}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className='custom-label'>Ειδικές κατηγορίες:</Form.Label>
                                <Form.Select
                                    className="custom-form"
                                    value={specialCategoriesIncluded.toString()}
                                    onChange={(e) => setSpecialCategoriesIncluded(e.target.value === 'true')}
                                    disabled={!educType}
                                >
                                    <option value="false">Όχι</option>
                                    <option value="true">Ναι</option>
                                </Form.Select>
                            </Form.Group>
                        </Form>
                        <div className="d-flex justify-content-center mt-3">
                            <Button
                                variant="secondary"
                                type="button"
                                className="predict-submit-button"
                                onClick={handleSubmit}
                                disabled={!isFormValid}
                            >
                                Υποβολή
                            </Button>
                        </div>
                </FilterSidebar>
                <div className={`ranking-per-state-main ${!sidebarOpen ? 'ranking-per-state-main--sidebar-collapsed' : ''}`}>
                    <Container fluid className="ranking-per-state-container">
                        <Row className="g-3 g-md-4">
                            <Col xs={12}>
                                <div className="ranking-per-state-content">
                                {!data && !loading && (
                                    <p className="text-muted small mb-2">
                                        Δεδομένα μεταθέσεων εκπαιδευτικών τρέχοντος έτους και προηγούμενων ετών</p>
                                )}
                                {loading && (
                                        <div className="text-center mt-3 mb-3">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div>Φόρτωση αποτελεσμάτων...</div>
                                        </div>
                                    )}

                                    {data ? (
                                        <div className={!sidebarOpen ? 'results-snug-mobile' : ''}>
                                            <div className="d-flex align-items-center my-3">
                                                <h5 className="text-center flex-grow-1 m-0">Αποτελέσματα</h5>
                                            </div>
                                            <div className='text-muted fst-italic small pb-2'>
                                                *αριθμός μεταθέσεων (ελάχιστα μόρια - μέγιστα μόρια)
                                            </div>
                                            <div className='table-outer-wrapper'>
                                                <div className="table-scroll-wrapper">
                                                    <Table bordered className="historical-results-table">
                                                        <thead>
                                                            <tr>
                                                                <th></th>
                                                                {uniqueDates.map(date => (
                                                                    <th key={date} className="no-wrap">{date}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {uniqueLocations.map(location => (
                                                                <tr key={location} className="cell-wrap">
                                                                    <td>{location}</td>
                                                                    {uniqueDates.map(date => {
                                                                        const record = data.find(d => d.date === date && d.location === location);
                                                                        return (
                                                                            <td key={date}>
                                                                                {record ? `${record.count} (${record.min}-${record.max})` : '—'}
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        </div>
                                    ) : !loading && null}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </main>
            <Footer />
        </div>
    )

}

export default TransferData;